import { Head } from 'vite-react-ssg';
import { TrackedLink } from "@/components/TrackedLink";
import { Link } from "react-router-dom";
import { Brain, Sparkles, Zap, DollarSign, CheckCircle, XCircle, Chrome } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import Breadcrumbs from "@/components/Breadcrumbs";

const DeepseekVsMistral = () => {
  const faqItems = [
    {
      question: "What is the main difference between DeepSeek and Mistral?",
      answer: "DeepSeek R1 is optimized for advanced reasoning and chain-of-thought capabilities, excelling in math and coding. Mistral focuses on efficiency and speed, offering models that balance performance with lower computational requirements. Both are open-source but target different use cases."
    },
    {
      question: "Which is better for coding: DeepSeek or Mistral?",
      answer: "DeepSeek is generally superior for coding tasks, especially DeepSeek Coder and R1. Mistral Codestral is competitive but DeepSeek's reasoning capabilities give it an edge for complex debugging and problem-solving."
    },
    {
      question: "Is DeepSeek or Mistral more cost-effective?",
      answer: "Both are highly cost-effective. DeepSeek's API is slightly cheaper, but Mistral's efficient models require less compute for local deployment. For API usage, DeepSeek wins on price; for local deployment, Mistral's smaller models may be more practical."
    },
    {
      question: "Can I run DeepSeek and Mistral locally?",
      answer: "Yes, both are open-source. Mistral's smaller models (7B, 8x7B) are easier to run on consumer hardware. DeepSeek's distilled models also work well locally, but full R1 requires significant resources."
    },
    {
      question: "Which model is faster: DeepSeek or Mistral?",
      answer: "Mistral models are generally faster due to their efficient architecture. DeepSeek R1's reasoning process takes longer but produces more thorough results. For quick responses, Mistral wins; for complex problems, DeepSeek is worth the wait."
    },
    {
      question: "Where are DeepSeek and Mistral based?",
      answer: "DeepSeek is a Chinese company based in Hangzhou. Mistral AI is a French company based in Paris. Both offer alternatives to US-based AI companies and are committed to open-source development."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const comparisonSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "DeepSeek vs Mistral: European vs Chinese Open-Source AI Comparison 2025",
    "description": "Comprehensive comparison of DeepSeek and Mistral AI. Compare features, performance, pricing, and use cases.",
    "author": {
      "@type": "Organization",
      "name": "The Independent DeepSeek Guide"
    },
    "publisher": {
      "@type": "Organization",
      "name": "The Independent DeepSeek Guide"
    },
    "datePublished": "2025-01-01",
    "dateModified": "2025-12-21"
  };

  return (
    <>
      <Head>
        <title>DeepSeek vs Mistral (2025): Open-Source AI Comparison</title>
        <meta 
          name="description" 
          content="DeepSeek vs Mistral comparison. DeepSeek excels at reasoning and coding. Mistral leads in efficiency and speed. Compare these open-source AI alternatives." 
        />
        <meta name="keywords" content="DeepSeek vs Mistral, Mistral vs DeepSeek, open-source AI comparison, European AI, Chinese AI, AI comparison 2025" />
        <link rel="canonical" href="https://deepseek.ai/deepseek-vs-mistral" />
        
        <meta property="og:title" content="DeepSeek vs Mistral (2026) — Open-Source AI Comparison" />
        <meta property="og:description" content="DeepSeek vs Mistral in 2026: full open-source AI comparison across reasoning, efficiency, multilingual support, pricing, self-hosting cost and the best model for each use case." />
        <meta property="og:url" content="https://deepseek.ai/deepseek-vs-mistral" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DeepSeek vs Mistral (2026) — Open-Source AI Comparison" />
        <meta name="twitter:description" content="DeepSeek vs Mistral in 2026: reasoning vs efficiency, multilingual, pricing, self-hosting and the best fit per use case." />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(comparisonSchema)}
        </script>
      </Head>

      <main className="min-h-screen pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <Breadcrumbs 
          items={[{ label: "Compare", path: "/deepseek-vs-chatgpt" }]} 
          currentPage="DeepSeek vs Mistral" 
        />

        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">DeepSeek vs Mistral</h1>
          <p className="text-xl text-muted-foreground">
            Chinese reasoning powerhouse vs French efficiency champion. Two open-source alternatives to Big Tech AI.
          </p>
        </div>

        {/* Quick Comparison */}
        <section className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-8 rounded-xl bg-card shadow-lg border">
              <div className="flex items-center mb-4">
                <Brain className="h-10 w-10 text-primary mr-3" />
                <h2 className="text-2xl font-bold">DeepSeek</h2>
              </div>
              <p className="text-muted-foreground mb-4">
                Chinese open-source model specializing in deep reasoning, mathematics, and code generation with visible chain-of-thought.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Superior reasoning & chain-of-thought
                </li>
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Best-in-class code generation
                </li>
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  MIT license - fully open
                </li>
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Lowest API pricing
                </li>
              </ul>
            </div>

            <div className="p-8 rounded-xl bg-card shadow-lg border">
              <div className="flex items-center mb-4">
                <Sparkles className="h-10 w-10 text-orange-500 mr-3" />
                <h2 className="text-2xl font-bold">Mistral</h2>
              </div>
              <p className="text-muted-foreground mb-4">
                French open-source models known for efficiency, speed, and excellent performance-to-size ratio.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Fast inference speed
                </li>
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Efficient architecture (MoE)
                </li>
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Runs on consumer hardware
                </li>
                <li className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Strong multilingual support
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Detailed Comparison Table */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Feature Comparison</h2>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Feature</TableHead>
                  <TableHead className="text-center">DeepSeek</TableHead>
                  <TableHead className="text-center">Mistral</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">Headquarters</TableCell>
                  <TableCell className="text-center">Hangzhou, China</TableCell>
                  <TableCell className="text-center">Paris, France</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Primary Strength</TableCell>
                  <TableCell className="text-center">Reasoning & Coding</TableCell>
                  <TableCell className="text-center">Efficiency & Speed</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Open Source</TableCell>
                  <TableCell className="text-center"><CheckCircle className="h-5 w-5 text-green-500 mx-auto" /></TableCell>
                  <TableCell className="text-center"><CheckCircle className="h-5 w-5 text-green-500 mx-auto" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">License</TableCell>
                  <TableCell className="text-center">MIT</TableCell>
                  <TableCell className="text-center">Apache 2.0</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Chain-of-Thought</TableCell>
                  <TableCell className="text-center"><CheckCircle className="h-5 w-5 text-green-500 mx-auto" /></TableCell>
                  <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Math & Reasoning</TableCell>
                  <TableCell className="text-center">Excellent</TableCell>
                  <TableCell className="text-center">Good</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Code Generation</TableCell>
                  <TableCell className="text-center">Excellent</TableCell>
                  <TableCell className="text-center">Good (Codestral)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Inference Speed</TableCell>
                  <TableCell className="text-center">Moderate</TableCell>
                  <TableCell className="text-center">Fast</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">API Pricing</TableCell>
                  <TableCell className="text-center">$0.14-0.28/M tokens</TableCell>
                  <TableCell className="text-center">$0.20-1.00/M tokens</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Flagship Model</TableCell>
                  <TableCell className="text-center">DeepSeek R1</TableCell>
                  <TableCell className="text-center">Mistral Large</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </section>

        {/* Use Cases */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">When to Use Each</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-6 rounded-xl bg-card shadow-lg border">
              <h3 className="text-xl font-semibold mb-4 flex items-center">
                <Brain className="h-6 w-6 text-primary mr-2" />
                Choose DeepSeek When...
              </h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                  Complex coding and debugging tasks
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                  Mathematical problem solving
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                  Need visible reasoning steps
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                  Cost is the primary concern
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                  Quality over speed
                </li>
              </ul>
            </div>

            <div className="p-6 rounded-xl bg-card shadow-lg border">
              <h3 className="text-xl font-semibold mb-4 flex items-center">
                <Sparkles className="h-6 w-6 text-orange-500 mr-2" />
                Choose Mistral When...
              </h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                  Speed is critical
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                  Running on limited hardware
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                  Multilingual applications
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                  EU data residency required
                </li>
                <li className="flex items-start">
                  <Zap className="h-5 w-5 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                  General-purpose chat applications
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Pricing Comparison */}
        <section className="mb-16 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">Pricing Comparison</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 rounded-xl bg-card shadow-lg border">
              <div className="flex items-center mb-4">
                <DollarSign className="h-6 w-6 text-green-500 mr-2" />
                <h3 className="text-xl font-semibold">DeepSeek Pricing</h3>
              </div>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Free tier available</li>
                <li>• R1: ~$0.14/million input tokens</li>
                <li>• ~$0.28/million output tokens</li>
                <li>• Free local deployment</li>
              </ul>
            </div>

            <div className="p-6 rounded-xl bg-card shadow-lg border">
              <div className="flex items-center mb-4">
                <DollarSign className="h-6 w-6 text-orange-500 mr-2" />
                <h3 className="text-xl font-semibold">Mistral Pricing</h3>
              </div>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Free tier available</li>
                <li>• Large: ~$2/million input tokens</li>
                <li>• Small: ~$0.20/million tokens</li>
                <li>• Free local deployment</li>
              </ul>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-16 max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
                <AccordionContent>{item.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* Related Comparisons */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">More AI Comparisons</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/deepseek-vs-chatgpt" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-semibold mb-2">DeepSeek vs ChatGPT</h3>
              <p className="text-muted-foreground">Compare with OpenAI's flagship model.</p>
            </Link>
            <Link to="/deepseek-vs-claude" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-semibold mb-2">DeepSeek vs Claude</h3>
              <p className="text-muted-foreground">Compare with Anthropic's Claude.</p>
            </Link>
            <Link to="/deepseek-vs-llama" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-semibold mb-2">DeepSeek vs Llama</h3>
              <p className="text-muted-foreground">Compare with Meta's open-source model.</p>
            </Link>
            <Link to="/deepseek-vs-stepfun" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-semibold mb-2">DeepSeek vs Stepfun</h3>
              <p className="text-muted-foreground">Compare with Stepfun's Step-3.5 Flash multimodal lab.</p>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <h2 className="text-2xl font-bold mb-4">Try DeepSeek AI Today</h2>
          <p className="text-muted-foreground mb-6">Experience the power of advanced AI reasoning for free.</p>
          <a
            href="https://chromewebstore.google.com/detail/ai-sidebar-with-deepseek/inhcgfpbfdjbjogdfjbclgolkmhnooop"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 text-lg font-medium rounded-full text-white bg-primary hover:bg-primary/90 transition-all shadow-lg"
          >
            <Chrome className="mr-2 h-5 w-5" />
            Add to Chrome - It's Free
          </a>
        </section>
        {/* Explore further - tracked CTAs */}
        <section className="mt-12 mb-12 p-6 rounded-xl bg-muted/40 border text-center">
          <h3 className="text-lg font-semibold mb-2">Want to go deeper?</h3>
          <p className="text-sm text-muted-foreground mb-4">See what DeepSeek actually costs and how to integrate it.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <TrackedLink
              to="/pricing"
              source="deepseek-vs-mistral"
              className="inline-flex items-center px-5 py-2.5 text-sm font-medium rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              See DeepSeek pricing →
            </TrackedLink>
            <TrackedLink
              to="/docs"
              source="deepseek-vs-mistral"
              className="inline-flex items-center px-5 py-2.5 text-sm font-medium rounded-full border-2 border-primary text-primary bg-background hover:bg-accent transition-colors"
            >
              Read the API docs →
            </TrackedLink>
          </div>
        </section>
      </main>
    </>
  );
};

export default DeepseekVsMistral;
